import { useCallback, useEffect, useRef, useState } from "react";
import { TouchPad, isTouchDevice } from "./components/TouchControls";
import "@/App.css";
import { GameCanvas } from "./components/GameCanvas";
import { Hud } from "./components/Hud";
import { StartScreen, Controls } from "./components/StartScreen";
import { EndModal } from "./components/EndModal";
import { Leaderboard } from "./components/Leaderboard";
import { saveScore, fetchMe, logout, deleteAccount } from "./lib/api";
import { useLocation } from "react-router-dom";
import { AuthCallback, startGoogleLogin } from "./components/AuthCallback";

const PENDING_KEY = "labirinto-pending";
import { LEVELS } from "./game/maze";
import { LangContext, STRINGS, initialLang } from "./i18n";
import { sfx } from "./game/sound";

const goFullscreen = () => {
  const el = document.documentElement;
  if (document.fullscreenElement || !el.requestFullscreen) return;
  el.requestFullscreen()
    .then(() => window.screen.orientation?.lock?.("landscape"))
    .catch(() => {});
};

export default function App() {
  const [screen, setScreen] = useState("start");
  const [name, setName] = useState(() => localStorage.getItem("labirinto-name") || "");
  const [level, setLevel] = useState(() => (LEVELS[localStorage.getItem("labirinto-level")] ? localStorage.getItem("labirinto-level") : "normale"));
  const [lang, setLangState] = useState(initialLang);
  const [seed, setSeed] = useState(0);
  const [stats, setStats] = useState({ time: 0, doors: 0 });
  const [paused, setPaused] = useState(false);
  const [end, setEnd] = useState(null);
  const [board, setBoard] = useState(false);
  const [user, setUser] = useState(undefined);
  const [notice, setNotice] = useState(null);
  const location = useLocation();

  const onUser = useCallback(async (u) => {
    setUser(u);
    if (!u) return;
    setName((n) => n.trim() || u.name.split(" ")[0].slice(0, 20));
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PENDING_KEY);
    try {
      const p = JSON.parse(raw);
      const r = await saveScore(p);
      setNotice({ rank: r.rank, level: p.difficulty, time: p.time_ms });
    } catch {
      setNotice(null);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    fetchMe().then(onUser).catch(() => setUser(null));
  }, [onUser]);

  const doLogout = () => logout().finally(() => setUser(null));
  const doDelete = () => deleteAccount().then(() => setUser(null));

  const loginAndSave = () => {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(end.pending));
    startGoogleLogin();
  };
  const [touch] = useState(isTouchDevice);
  const [muted, setMuted] = useState(sfx.muted);
  const inputRef = useRef(null);

  useEffect(() => {
    const unlock = () => sfx.init();
    ["pointerdown", "touchend", "keydown"].forEach((ev) => window.addEventListener(ev, unlock));
    return () => ["pointerdown", "touchend", "keydown"].forEach((ev) => window.removeEventListener(ev, unlock));
  }, []);

  const t = STRINGS[lang];
  const setLang = (l) => {
    localStorage.setItem("labirinto-lang", l);
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t.title;
  }, [lang, t]);

  const toggleMute = () => {
    sfx.setMuted(!muted);
    setMuted(!muted);
  };

  const start = useCallback(() => {
    sfx.init();
    if (touch) goFullscreen();
    localStorage.setItem("labirinto-name", name.trim());
    localStorage.setItem("labirinto-level", level);
    setSeed((s) => s + 1);
    setStats({ time: 0, doors: 0 });
    setEnd(null);
    setPaused(false);
    setNotice(null);
    setScreen("playing");
  }, [name, touch, level]);

  const onEnd = useCallback(
    async (type, s) => {
      setEnd({ type, stats: s, level });
      setScreen("ended");
      if (type !== "exit") return;
      const payload = { name: name.trim() || "Anonimo", time_ms: s.time, doors: s.doors, difficulty: level };
      if (!user) {
        setEnd((e) => ({ ...e, needsLogin: true, pending: payload }));
        return;
      }
      try {
        const r = await saveScore(payload);
        setEnd((e) => ({ ...e, rank: r.rank }));
      } catch {
        setEnd((e) => ({ ...e, saveError: true }));
      }
    },
    [name, level, user]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT") return;
      if (screen === "playing" && (e.code === "Escape" || e.code === "KeyP")) setPaused((p) => !p);
      if (screen === "ended" && e.code === "Enter") start();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [screen, start]);

  const running = screen === "playing" && !paused && !board;

  if (location.hash?.includes("session_id=")) return <AuthCallback onUser={onUser} />;

  return (
    <LangContext.Provider value={lang}>
    <main className={`app ${touch ? "touch" : ""}`} data-testid="app-root">
      <div className="stage-wrap">
        <Hud
          stats={stats}
          paused={paused}
          canPause={screen === "playing"}
          onPause={() => setPaused((p) => !p)}
          onBoard={() => setBoard(true)}
          muted={muted}
          onMute={toggleMute}
          level={level}
        />
        <div className="play-area">
        {touch && <TouchPad side="left" input={inputRef} />}
        <div className="stage">
          <GameCanvas seed={seed} level={level} running={running} onStats={setStats} onEnd={onEnd} inputRef={inputRef} touch={touch} lang={lang} />
          {screen === "start" && (
            <StartScreen name={name} setName={setName} level={level} setLevel={setLevel} lang={lang} setLang={setLang} user={user} onLogout={doLogout} onDelete={doDelete} notice={notice} onStart={start} onBoard={() => setBoard(true)} />
          )}
          {screen === "playing" && paused && (
            <div className="overlay" data-testid="pause-overlay">
              <div className="panel panel-enter">
                <h2 className="title">{t.paused}</h2>
                <div className="row">
                  <button className="btn-primary" onClick={() => setPaused(false)} data-testid="resume-button">{t.resume}</button>
                  <button className="btn-ghost" onClick={start} data-testid="pause-restart-button">{t.newMaze}</button>
                  <button className="btn-ghost" onClick={() => { setPaused(false); setScreen("start"); }} data-testid="pause-menu-button">{t.backToMenu}</button>
                </div>
              </div>
            </div>
          )}
          {screen === "ended" && end && <EndModal end={end} onLoginSave={loginAndSave} onRestart={start} onMenu={() => setScreen("start")} onBoard={() => setBoard(true)} />}
          {board && <Leaderboard level={level} onClose={() => setBoard(false)} />}
        </div>
        {touch && <TouchPad side="right" input={inputRef} />}
        </div>
        <p className="rotate-hint" data-testid="rotate-hint">{t.rotate}</p>
        <div className="footer">
          <Controls />
          <span className="footer-note">{t.footer} · <a href="/privacy.html" target="_blank" rel="noreferrer" data-testid="privacy-link">{t.privacy}</a></span>
        </div>
      </div>
    </main>
    </LangContext.Provider>
  );
}
